import React from 'react';
import { connect } from 'react-redux';
import { isMobileBrowser } from '../../base/environment/utils';
import { translate, translateToHTML } from '../../base/i18n/functions';
import Icon from '../../base/icons/components/Icon';
import { IconWarning } from '../../base/icons/svg';
import Watermarks from '../../base/react/components/web/Watermarks';
import getUnsafeRoomText from '../../base/util/getUnsafeRoomText.web';
import CalendarList from '../../calendar-sync/components/CalendarList.web';
import RecentList from '../../recent-list/components/RecentList.web';
import SettingsButton from '../../settings/components/web/SettingsButton';
import { SETTINGS_TABS } from '../../settings/constants';
import { AbstractWelcomePage, _mapStateToProps } from './AbstractWelcomePage';
import Tabs from './Tabs';
/**
 * The pattern used to validate room name.
 *
 * @type {string}
 */
export const ROOM_NAME_VALIDATE_PATTERN_STR = '^[^?&:\u0022\u0027%#]+$';
/**
 * The Web container rendering the welcome page.
 *
 * @augments AbstractWelcomePage
 */
class WelcomePage extends AbstractWelcomePage {
    /**
     * Initializes a new WelcomePage instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            generateRoomNames: interfaceConfig.GENERATE_ROOMNAMES_ON_WELCOME_PAGE
        };
        /**
      * Used To display a warning massage if the title input has no allow character.
      *
      * @private
      * @type {boolean}
      */
        this._titleHasNotAllowCharacter = false;
        /**
         * The HTML Element used as the container for additional content. Used
         * for directly appending the additional content template to the dom.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalContentRef = null;
        this._roomInputRef = null;
        /**
         * The HTML Element used as the container for additional toolbar content. Used
         * for directly appending the additional content template to the dom.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalToolbarContentRef = null;
        this._additionalCardRef = null;
        /**
         * The template to use as the additional card displayed near the main one.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalCardTemplate = document.getElementById('welcome-page-additional-card-template');
        /**
         * The template to use as the main content for the welcome page. If
         * not found then only the welcome page head will display.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalContentTemplate = document.getElementById('welcome-page-additional-content-template');
        /**
         * The template to use as the additional content for the welcome page header toolbar.
         * If not found then only the settings icon will be displayed.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalToolbarContentTemplate = document.getElementById('settings-toolbar-additional-content-template');
        // Bind event handlers so they are only bound once per instance.
        this._onFormSubmit = this._onFormSubmit.bind(this);
        this._onRoomChange = this._onRoomChange.bind(this);
        this._setAdditionalCardRef = this._setAdditionalCardRef.bind(this);
        this._setAdditionalContentRef
            = this._setAdditionalContentRef.bind(this);
        this._setRoomInputRef = this._setRoomInputRef.bind(this);
        this._setAdditionalToolbarContentRef
            = this._setAdditionalToolbarContentRef.bind(this);
        this._renderFooter = this._renderFooter.bind(this);
    }
    /**
     * Implements React's {@link Component#componentDidMount()}. Invoked
     * immediately after this component is mounted.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        super.componentDidMount();
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css';
        document.head.appendChild(link);
        document.body.classList.add('welcome-page');
        document.title = interfaceConfig.APP_NAME;
        if (this.state.generateRoomNames) {
            this._updateRoomName();
        }
        if (this._shouldShowAdditionalContent()) {
            this._additionalContentRef?.appendChild(this._additionalContentTemplate?.content.cloneNode(true));
        }
        if (this._shouldShowAdditionalToolbarContent()) {
            this._additionalToolbarContentRef?.appendChild(this._additionalToolbarContentTemplate?.content.cloneNode(true));
        }
        if (this._shouldShowAdditionalCard()) {
            this._additionalCardRef?.appendChild(this._additionalCardTemplate?.content.cloneNode(true));
        }
    }
    /**
     * Removes the classname used for custom styling of the welcome page.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        super.componentWillUnmount();
        const link = document.querySelector('link[href="https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css"]');
        if (link) {
            document.head.removeChild(link);
        }
        document.body.classList.remove('welcome-page');
    }
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement|null}
     */
    render() {
        const { _moderatedRoomServiceUrl, t } = this.props;
        const { DEFAULT_WELCOME_PAGE_LOGO_URL, DISPLAY_WELCOME_FOOTER } = interfaceConfig;
        const showAdditionalCard = this._shouldShowAdditionalCard();
        const showAdditionalContent = this._shouldShowAdditionalContent();
        const showAdditionalToolbarContent = this._shouldShowAdditionalToolbarContent();
        const contentClassName = showAdditionalContent ? 'with-content' : 'without-content';
        const footerClassName = DISPLAY_WELCOME_FOOTER ? 'with-footer' : 'without-footer';
        return (React.createElement("div", { className: `welcome ${contentClassName} ${footerClassName}`, id: 'welcome_page' },
            React.createElement("div", { className: 'welcome-watermark' },
                React.createElement(Watermarks, { defaultJitsiLogoURL: DEFAULT_WELCOME_PAGE_LOGO_URL })),
            React.createElement("div", { className: 'header' },
                React.createElement("div", { className: 'welcome-page-settings' },
                    React.createElement(SettingsButton, { defaultTab: SETTINGS_TABS.CALENDAR, isDisplayedOnWelcomePage: true }),
                    showAdditionalToolbarContent
                        ? React.createElement("div", { className: 'settings-toolbar-content', ref: this._setAdditionalToolbarContentRef })
                        : null),
                React.createElement("div", { className: 'header-image' }),
                React.createElement("div", { className: 'header-container' },
                    React.createElement("h1", { className: 'header-text-title' }, "Smart Parley Meet"),
                    React.createElement("span", { className: 'header-text-subtitle' }, "Digitale WEG-Versammlung"),
                    React.createElement("div", { className: 'welcome-register-btn' },
                        React.createElement("a", { style: { color: '#00e7c2' }, href: 'https://app.smartparley.de/register' }, "Registrieren")),
                    _moderatedRoomServiceUrl && (React.createElement("div", { id: 'moderated-meetings' },
                        React.createElement("p", null, translateToHTML(t, 'welcomepage.moderatedMessage', { url: _moderatedRoomServiceUrl })))))),
            React.createElement("div", { className: 'welcome-cards-container' },
                React.createElement("div", { className: 'welcome-row' },
                    React.createElement("hr", null),
                    React.createElement("div", { className: 'row' },
                        React.createElement("div", { className: 'col-md-12' },
                            React.createElement("h4", null,
                                React.createElement("div", { className: 'w-header header-1' }, "SmartParley bringt die Eigent\u00FCmer einer Immobilie einfach und effektiv zusammen."),
                                React.createElement("div", { className: 'w-header header-2' }, "Unsere Service - Plattform befindet sich aktuell im Aufbau und wird in K\u00FCrze folgende Funktionen bieten."),
                                React.createElement("div", { className: 'w-header header-3' }, "Jederzeit . Rechtskonform . Transparent")))),
                    React.createElement("div", { className: 'row' },
                        React.createElement("div", { className: 'col-md-3 box' },
                            React.createElement("div", null,
                                React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "30%", viewBox: "0 0 100 100", id: "1064555541", className: "svg u_1064555541", "data-icon-name": "li_key" },
                                    " ",
                                    React.createElement("g", null,
                                        " ",
                                        React.createElement("path", { d: "M71.7,15.7L37.9,49.5c-1.5-0.4-3-0.6-4.5-0.6c-9.8,0-17.7,7.9-17.7,17.7c0,9.8,7.9,17.7,17.7,17.7c9.8,0,17.7-7.9,17.7-17.7c0-1.5-0.2-3-0.6-4.5l11.6-11.6V40h11.1V29h11.1V15.7H71.7z M82.1,26.8H71v11.1H60v11.7L48,61.5l0.2,0.6c0.4,1.5,0.7,3,0.7,4.5c0,8.5-6.9,15.5-15.5,15.5c-8.5,0-15.5-6.9-15.5-15.5c0-8.5,6.9-15.5,15.5-15.5c1.5,0,3,0.2,4.5,0.7l0.6,0.2l34-34h9.5V26.8z" }),
                                        React.createElement("circle", { cx: "33.4", cy: "66.6", r: "2.2" })))),
                            React.createElement("h4", null, "Eigener Datenbereich f\u00FCr Sondereigentum"),
                            React.createElement("div", { className: 'box-content' }, "SmartParley bietet jeder Nutzer*in die M\u00F6glichkeit, ihre*seine Daten f\u00FCr die eigene Immobilie in einem privaten Bereich sicher abzulegen. Und zwar kostenfrei.")),
                        React.createElement("div", { className: 'col-md-3 box' },
                            React.createElement("div", null,
                                React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "30%", viewBox: "0 0 100 100", id: "1104160192", className: "svg u_1104160192", "data-icon-name": "business_checklist_checkmark" },
                                    " ",
                                    React.createElement("path", { d: "M30.5,32.13l-3.29,3.29-1-1a1.3,1.3,0,0,0-1.83,1.83l1.33,1.33a2.07,2.07,0,0,0,2.92,0L32.34,34a1.3,1.3,0,1,0-1.83-1.83Z" }),
                                    React.createElement("path", { d: "M35.3,36.29H55a1.3,1.3,0,0,0,0-2.59H35.3a1.3,1.3,0,0,0,0,2.59Z" }),
                                    React.createElement("path", { d: "M30.5,43.31,27.21,46.6l-1-1a1.3,1.3,0,0,0-1.83,1.83l1.33,1.33a2.07,2.07,0,0,0,2.92,0l3.66-3.66a1.3,1.3,0,1,0-1.83-1.83Z" }),
                                    React.createElement("path", { d: "M30.5,54.48l-3.29,3.29-1-1a1.3,1.3,0,1,0-1.83,1.83L25.75,60a2.07,2.07,0,0,0,2.92,0l3.66-3.66a1.3,1.3,0,1,0-1.83-1.83Z" }),
                                    React.createElement("path", { d: "M61.93,37.82V26.6a6.27,6.27,0,0,0-6.26-6.26h-31a6.27,6.27,0,0,0-6.26,6.26V65.74A6.27,6.27,0,0,0,24.69,72H44.45A20.93,20.93,0,1,0,61.93,37.82ZM24.69,69.4A3.67,3.67,0,0,1,21,65.74V26.6a3.67,3.67,0,0,1,3.66-3.66h31a3.67,3.67,0,0,1,3.66,3.66V37.82a20.9,20.9,0,0,0-14.39,7H35.3a1.3,1.3,0,0,0,0,2.59H43A20.79,20.79,0,0,0,39.87,56H35.3a1.3,1.3,0,0,0,0,2.59H39.7s0,.05,0,.08A20.81,20.81,0,0,0,42.63,69.4Zm35.95,7.66A18.34,18.34,0,1,1,79,58.72,18.37,18.37,0,0,1,60.63,77.06Z" }),
                                    React.createElement("path", { d: "M55.88,68.39a4.4,4.4,0,0,1-3.13-1.3l-5.41-5.41a1.3,1.3,0,1,1,1.83-1.83l5.41,5.41a1.84,1.84,0,0,0,2.6,0L72.1,50.34a1.3,1.3,0,1,1,1.83,1.83L59,67.09A4.4,4.4,0,0,1,55.88,68.39Z" }))),
                            React.createElement("h4", null, "Abstimm- und Kommentar-funktion"),
                            React.createElement("div", { className: 'box-content' }, "Hochgeladene Dokumente k\u00F6nnen von allen Parley-Teilnehmern innerhalb einer Gemeinschaft eingesehen und kommentiert werden.")),
                        React.createElement("div", { className: 'col-md-3 box' },
                            React.createElement("div", null,
                                React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "30%", viewBox: "0 0 100 100", id: "1753115037", className: "svg u_1753115037", "data-icon-name": "business_headphones_mic" },
                                    " ",
                                    React.createElement("path", { d: "M92.36,56.19a11,11,0,0,0-7.2-10.34,35.16,35.16,0,0,0-70.32,0,11,11,0,0,0,3.83,21.37H20a4.09,4.09,0,0,0,8.17-.16V45.32A4.09,4.09,0,0,0,20,45.16h-1.3A11,11,0,0,0,17,45.3a33,33,0,0,1,66,0,11,11,0,0,0-1.67-.14H80a4.09,4.09,0,0,0-8.17.16V67.07a4.1,4.1,0,0,0,4.09,4.09,4,4,0,0,0,1.89-.49A16.85,16.85,0,0,1,61.32,86.56a4.18,4.18,0,0,0,.56-2.07,4.24,4.24,0,0,0-4.24-4.24H53.82a4.24,4.24,0,0,0,0,8.47H61a19,19,0,0,0,19-19v-2.5h1.29A11.05,11.05,0,0,0,92.36,56.19ZM24.06,43.38A1.94,1.94,0,0,1,26,45.32V67.07a1.94,1.94,0,0,1-3.88,0V45.32A1.94,1.94,0,0,1,24.06,43.38ZM20,47.31V65.08H18.68a8.88,8.88,0,0,1,0-17.77Zm37.5,39.27H53.82a2.08,2.08,0,0,1,0-4.17h3.83a2.08,2.08,0,0,1,0,4.17ZM75.94,69A1.94,1.94,0,0,1,74,67.07V45.32a1.94,1.94,0,1,1,3.88,0V67.07A1.94,1.94,0,0,1,75.94,69Zm5.38-3.93H80V47.31h1.29a8.88,8.88,0,1,1,0,17.77Z", id: "1432485522" }),
                                    React.createElement("path", { d: "M43.59,57.27a1.08,1.08,0,0,0,0-2.15H41.4a1.08,1.08,0,0,0,0,2.15Z", id: "1366951990" }),
                                    React.createElement("path", { d: "M51.1,57.27a1.08,1.08,0,0,0,0-2.15H48.9a1.08,1.08,0,0,0,0,2.15Z", id: "1462274599" }),
                                    React.createElement("path", { d: "M58.6,57.27a1.08,1.08,0,0,0,0-2.15h-2.2a1.08,1.08,0,0,0,0,2.15Z", id: "1538392645" }))),
                            React.createElement("h4", null, "Meetings, jederzeit und \u00FCberall"),
                            React.createElement("div", { className: 'box-content' }, "Egal ob direkt vor Ort, per Videokonferenz, mit dem Telefon oder schriftlich per Chat:")),
                        React.createElement("div", { className: 'col-md-3 box' },
                            React.createElement("div", null,
                                React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "30%", viewBox: "0 0 100 100", id: "1692178893", className: "svg u_1692178893", "data-icon-name": "business_graph_up_down" },
                                    " ",
                                    React.createElement("path", { d: "M15.54,81.67H86.62L86.53,64,78.2,71.66,72.94,66.4,69.6,69.75V52.84L59.3,63.14V58.48l-6.14-7.92-10.32,9-3.35-4.14-10.24,8v-21l-5.17,4L20.68,41l-3.49,3.8V42.4L26,32.22,38.93,52.29,53.81,40.12l9.54,11,8.54-7L77.57,52l-2.48.26,6.71,4.15-1.89-7.67-1,2.28-6.7-9.21-8.65,7.07L54,37.82l-14.69,12L26.24,29.47,17.19,39.88V24.39l2.16,1.25-3-7.31-3,7.31,2.16-1.25V81.67Zm4.9-37.9,3.21,5.13,4-3.07v21l11.63-9.12,3.41,4.2,10.29-9L57.64,59v8.08l10.3-10.3V73.74l5-5L78.15,74l6.74-6.23L85,80H17.19V47.29Z" }))),
                            React.createElement("h4", null, "Alle Kosten auf einen Blick"),
                            React.createElement("div", { className: 'box-content' }, "SmartParley erm\u00F6glicht es alle Kosten, die sowohl das Gemeinschafts- als auch das Sondereigentum betreffen, sowohl tabellarisch als auch graphisch darzustellen."))),
                    React.createElement("div", { className: 'row mt-5 mb-5' },
                        React.createElement("div", { className: 'col-md-6 mt-3' },
                            React.createElement("iframe", { width: "100%", height: '220px', src: "https://www.youtube.com/embed/wClxoHHV_H4", frameBorder: "0", allowFullScreen: true })),
                        React.createElement("div", { className: 'col-md-6 mt-3' },
                            React.createElement("h5", { style: { color: '#000' } }, "Registriere dich jetzt und erhalte weitere Informationen \u00FCber die Entwicklung unserer Plattform direkt per Email."),
                            React.createElement("div", null,
                                React.createElement("div", { className: 'welcome-register-btn mt-5' },
                                    React.createElement("a", { style: { backgroundColor: '#00e6c3', color: '#fff' }, href: 'https://app-test.smartparley.de/register' }, "Registrieren"))))))),
            DISPLAY_WELCOME_FOOTER && this._renderFooter()));
    }
    /**
     * Renders the insecure room name warning.
     *
     * @inheritdoc
     */
    _doRenderInsecureRoomNameWarning() {
        return (React.createElement("div", { className: 'insecure-room-name-warning' },
            React.createElement(Icon, { src: IconWarning }),
            React.createElement("span", null, getUnsafeRoomText(this.props.t, 'welcome'))));
    }
    /**
     * Prevents submission of the form and delegates join logic.
     *
     * @param {Event} event - The HTML Event which details the form submission.
     * @private
     * @returns {void}
     */
    _onFormSubmit(event) {
        event.preventDefault();
        if (!this._roomInputRef || this._roomInputRef.reportValidity()) {
            this._onJoin();
        }
    }
    /**
     * Overrides the super to account for the differences in the argument types
     * provided by HTML and React Native text inputs.
     *
     * @inheritdoc
     * @override
     * @param {Event} event - The (HTML) Event which details the change such as
     * the EventTarget.
     * @protected
     */
    // @ts-ignore
    // eslint-disable-next-line require-jsdoc
    _onRoomChange(event) {
        const specialCharacters = ['?', '&', ':', '\'', '"', '%', '#', '.'];
        this._titleHasNotAllowCharacter = specialCharacters.some(char => event.target.value.includes(char));
        super._onRoomChange(event.target.value);
    }
    /**
     * Renders the footer.
     *
     * @returns {ReactElement}
     */
    _renderFooter() {
        const { t, _deeplinkingCfg: { ios = { downloadLink: undefined }, android = {
            fDroidUrl: undefined,
            downloadLink: undefined
        } } } = this.props;
        const { downloadLink: iosDownloadLink } = ios;
        const { fDroidUrl, downloadLink: androidDownloadLink } = android;
        return (React.createElement("footer", { className: 'welcome-footer' },
            React.createElement("div", { className: 'welcome-footer-centered' },
                React.createElement("div", { className: 'welcome-footer-padded' },
                    React.createElement("div", { className: 'welcome-footer-row-block welcome-footer--row-1' },
                        "\u00A9 ",
                        new Date().getFullYear(),
                        ' ',
                        " Alle Rechte vorbehalten | ",
                        React.createElement("a", { href: 'https://www.smartparley.de', target: '_blank', rel: 'noopener noreferrer' }, "SmartParley GmbH"))))));
    }
    /**
     * Renders tabs to show previous meetings and upcoming calendar events. The
     * tabs are purposefully hidden on mobile browsers.
     *
     * @returns {ReactElement|null}
     */
    _renderTabs() {
        if (isMobileBrowser()) {
            return null;
        }
        const { _calendarEnabled, _recentListEnabled, t } = this.props;
        const tabs = [];
        if (_calendarEnabled) {
            tabs.push({
                id: 'calendar',
                label: t('welcomepage.upcomingMeetings'),
                content: React.createElement(CalendarList, null)
            });
        }
        if (_recentListEnabled) {
            tabs.push({
                id: 'recent',
                label: t('welcomepage.recentMeetings'),
                content: React.createElement(RecentList, null)
            });
        }
        if (tabs.length === 0) {
            return null;
        }
        return (React.createElement(Tabs, { accessibilityLabel: t('welcomepage.meetingsAccessibilityLabel'), tabs: tabs }));
    }
    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * additional card shown near the tabs card.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the welcome page content.
     * @private
     * @returns {void}
     */
    _setAdditionalCardRef(el) {
        this._additionalCardRef = el;
    }
    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * welcome page content.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the welcome page content.
     * @private
     * @returns {void}
     */
    _setAdditionalContentRef(el) {
        this._additionalContentRef = el;
    }
    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * toolbar additional content.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the additional toolbar content.
     * @private
     * @returns {void}
     */
    _setAdditionalToolbarContentRef(el) {
        this._additionalToolbarContentRef = el;
    }
    /**
     * Sets the internal reference to the HTMLInputElement used to hold the
     * welcome page input room element.
     *
     * @param {HTMLInputElement} el - The HTMLElement for the input of the room name on the welcome page.
     * @private
     * @returns {void}
     */
    _setRoomInputRef(el) {
        this._roomInputRef = el;
    }
    /**
     * Returns whether or not an additional card should be displayed near the tabs.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalCard() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_ADDITIONAL_CARD
            && this._additionalCardTemplate
            && this._additionalCardTemplate.content
            && this._additionalCardTemplate.innerHTML.trim();
    }
    /**
     * Returns whether or not additional content should be displayed below
     * the welcome page's header for entering a room name.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_CONTENT
            && this._additionalContentTemplate
            && this._additionalContentTemplate.content
            && this._additionalContentTemplate.innerHTML.trim();
    }
    /**
     * Returns whether or not additional content should be displayed inside
     * the header toolbar.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalToolbarContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT
            && this._additionalToolbarContentTemplate
            && this._additionalToolbarContentTemplate.content
            && this._additionalToolbarContentTemplate.innerHTML.trim();
    }
}
/**
 * Default values for {@code WelcomePage} component's properties.
 *
 * @static
 */
WelcomePage.defaultProps = {
    _room: ''
};
export default translate(connect(_mapStateToProps)(WelcomePage));
